import React, { useState, useRef, useMemo, useEffect } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { GetInstallationListAPI, fetchInstallationPlanQueryFn } from "../../../Redux/installationPlan";
import { useNavigate, useLocation } from "react-router-dom";
import { svg } from "../../../../Assets/svg";
import { Spin } from "antd";
import { decryptByText } from "../../../../helpers/bycrypt";
import { CapitalizeFirstWord } from "../../../../helpers/utils";
import { RootState, useAppSelector, useAppDispatch } from "../../../../store";
import FullScreenLoader from "../../../Components/FullScreenLoader";
import { useQuery } from "@tanstack/react-query";

dayjs.extend(utc);
interface InstalltionCalnderViewProps {
  statusFilter: string;
}

// Helper to convert hex colors to RGBA for elegant light tinted backgrounds
const hexToRgba = (hex: string, alpha: number) => {
  if (!hex) return `rgba(100, 116, 139, ${alpha})`;
  let c = hex.substring(1);
  if (c.length === 3) {
    c = c[0] + c[0] + c[1] + c[1] + c[2] + c[2];
  }
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const InstalltionCalnderView: React.FC<InstalltionCalnderViewProps> = ({
  statusFilter,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const calendarRef = useRef<any>(null);
  const [activeStartDate, setActiveStartDate] = useState<any>(
    location.state?.startDate || ""
  );
  const [activeEndDate, setActiveEndDate] = useState<any>(
    location.state?.endDate || ""
  );
  const [title, setTitle] = useState("");

  const { teamMemberList } = useAppSelector(
    (state: RootState) => state.teamMember,
  );

  const queryFilters = useMemo(
    () => ({
      limit: 1000,
      page: 1,
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(activeStartDate ? { start_at: activeStartDate } : {}),
      ...(activeEndDate ? { end_at: activeEndDate } : {}),
    }),
    [statusFilter, activeStartDate, activeEndDate],
  );

  const {
    data: calendarQueryResult,
    isLoading: getInstallationDataLoading,
  } = useQuery({
    queryKey: ["installationPlanCalendar", queryFilters],
    queryFn: ({ signal }) =>
      fetchInstallationPlanQueryFn(queryFilters, signal),
    refetchInterval: 5000,
    staleTime: 5000,
    enabled: !!activeStartDate && !!activeEndDate,
  });

  const installationPlanData = useMemo(() => {
    return calendarQueryResult?.data || [];
  }, [calendarQueryResult]);

  useEffect(() => {
    if (location.state?.currentDate && calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(location.state.currentDate);
    }
  }, [location.state?.currentDate]);

  // Events (Calendar Blocks)
  const events = useMemo(() => {
    if (!installationPlanData) return [];
    const itemsWithDates = installationPlanData.filter(
      (item: any) => item?.start_at && item?.end_at,
    );
    return itemsWithDates.map((item: any) => {
      const empColor = item?.employee_ids?.[0]?.color_code;

      // Custom styling based on employee color (first employee) or fall back to status
      let backColor = "#f3f4f6";
      let fontColor = "#4b5563";
      let borderColor = "#d1d5db";

      if (empColor) {
        backColor = hexToRgba(empColor, 0.12);
        fontColor = empColor;
        borderColor = empColor;
      }

      return {
        id: `event_${item._id}`,
        title: `${item.order_id?.order || "Job"} - ${
          item.order_id?.company || "N/A"
        }`,
        start: `${dayjs(item.start_at).format("YYYY-MM-DD")}T08:00:00`,
        end: `${dayjs(item.end_at).format("YYYY-MM-DD")}T17:00:00`,
        backgroundColor: backColor || "#989898",
        borderColor: borderColor || "#000000",
        textColor: fontColor,
        extendedProps: {
          data: item,
        },
      };
    });
  }, [installationPlanData]);


  const handleDatesSet = (dateInfo: any) => {
    setTitle(dateInfo.view.title);
    const startUTC = dayjs(dateInfo.view.currentStart)
      .startOf("month")
      .toISOString();
    const endUTC = dayjs(dateInfo.view.currentStart)
      .endOf("month")
      .toISOString();
    setActiveStartDate(startUTC);
    setActiveEndDate(endUTC);
  };

  const handleEventClick = (info: any) => {
    const item = info.event.extendedProps.data;
    navigate(`/installation-plan/view-detail/${item._id}`, {
      state: {
        activeTab: "calendar",
        startDate: activeStartDate,
        endDate: activeEndDate,
        currentDate: info.view.currentStart.toISOString(),
      },
    });
  };

  const renderEventContent = (eventInfo: any) => {
    const item = eventInfo.event.extendedProps.data;
    return (
      <div
        className="w-100 h-100 p-2 d-flex flex-column justify-content-between text-truncate transition-all"
        // style={{
        //   backgroundColor: eventInfo.event.backgroundColor,
        //   border: `1px solid ${eventInfo.event.borderColor}`,
        //   color: eventInfo.event.textColor,
        //   fontSize: "11px",
        //   lineHeight: "1.3",
        //   cursor: "pointer",
        // }}
      >
        <div className="fw-bold text-truncate">
          {item?.order_id?.customer_snapshot?.company_name || ""}
        </div>
      </div>
    );
  };

  return (
    <div>
      <style>{`
        /* FullCalendar Custom Theme Overrides */
        .fc {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
        }
        .fc-header-toolbar {
          margin-bottom: 24px !important;
          padding: 6px 4px !important;
        }
        .fc-toolbar-title {
          font-size: 1.4rem !important;
          font-weight: 700 !important;
          color: #0f172a !important;
          letter-spacing: -0.02em !important;
        }
        .fc-col-header-cell {
          background-color: #f8fafc !important;
          padding: 12px 0 !important;
          font-weight: 600 !important;
          font-size: 0.85rem !important;
          color: #475569 !important;
          border: 1px solid #f1f5f9 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.05em !important;
        }
        .fc-daygrid-day {
          transition: background-color 0.15s ease;
          border-color: #f1f5f9 !important;
        }
        .fc-daygrid-day:hover {
          background-color: #f8fafc !important;
          cursor: pointer;
        }
        .fc-daygrid-day-number {
          font-weight: 600 !important;
          font-size: 0.85rem !important;
          color: #64748b !important;
          padding: 10px !important;
          transition: color 0.15s ease !important;
        }
        .fc-daygrid-day:hover .fc-daygrid-day-number {
          color: #0f172a !important;
        }
        .fc-day-today {
          background-color: #f0fdf4 !important; /* Soft premium green background */
        }
        .fc-day-today .fc-daygrid-day-number {
          color: #16a34a !important;
          font-weight: 700 !important;
        }
        /* Style the grid borders */
        .fc-scrollgrid {
          border-radius: 12px !important;
          overflow: hidden !important;
          border: 1px solid #e2e8f0 !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.02) !important;
        }
        .fc-theme-standard td, .fc-theme-standard th {
          border: 1px solid #e2e8f0 !important;
        }
        
        /* Events styling overrides */
        .fc-daygrid-event {
          border-radius: 6px !important;
          padding: 6px 10px !important;
          margin: 3px 6px !important;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.03) !important;
          transition: transform 0.2s ease, box-shadow 0.2s ease !important;
          border-width: 1px !important;
        }
        .fc-daygrid-event:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 6px 12px -2px rgba(50, 50, 93, 0.08), 0 3px 7px -3px rgba(0, 0, 0, 0.08) !important;
        }
        
        /* Modern Buttons styling */
        .fc .fc-button {
          background-color: #231F20 !important;
          color: #ffffff !important;
          border: 1px solid #231F20 !important;
          padding: 8px 16px !important;
          font-size: 0.875rem !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
          transition: all 0.2s ease !important;
          text-transform: capitalize !important;
        }
        .fc .fc-button:hover {
          background-color: #3d3537 !important;
          border-color: #3d3537 !important;
          color: #ffffff !important;
        }
        .fc .fc-button:focus, .fc .fc-button:active {
          box-shadow: 0 0 0 3px rgba(35, 31, 32, 0.25) !important;
          border-color: #231F20 !important;
        }
        .fc .fc-button-primary:not(:disabled):active, 
        .fc .fc-button-primary:not(:disabled).fc-button-active {
          background-color: #1a1718 !important;
          border-color: #1a1718 !important;
          color: #ffffff !important;
        }
        .fc .fc-today-button {
          background-color: #231F20 !important;
          color: #ffffff !important;
          border-color: #231F20 !important;
        }
        .fc .fc-today-button:disabled {
          opacity: 0.55 !important;
          background-color: #231F20 !important;
          color: #ffffff !important;
          cursor: not-allowed !important;
        }
        /* Style the prev/next arrow alignment */
        .fc .fc-button .fc-icon {
          font-size: 1em !important;
          vertical-align: middle !important;
        }
        /* Calendar container styling */
        .month-calendar-container {
          background: #ffffff;
          border-radius: 16px;
          padding: 24px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.02), 0 4px 6px -2px rgba(0, 0, 0, 0.02);
        }
      `}</style>
      <>
        {/* Unique Installation Manager List */}
        {teamMemberList?.length > 0 && (
          <div
            className="d-flex flex-wrap align-items-center gap-4 mb-3 p-3 rounded shadow-sm border"
            style={{ backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}
          >
            {teamMemberList?.map((manager: any) => {
              return (
                <div
                  key={manager._id || manager.id}
                  className="d-flex align-items-center gap-2"
                >
                  <span
                    className="rounded-circle"
                    style={{
                      width: "12px",
                      height: "12px",
                      backgroundColor: manager?.color_code || "#000000",
                      display: "inline-block",
                    }}
                  />
                  <span
                    className="fw-semibold text-secondary"
                    style={{ fontSize: "14.5px" }}
                  >
                    {manager?.first_name || ""} {manager?.last_name || ""}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <div className="month-calendar-container" style={{ position: "relative" }}>
          {getInstallationDataLoading && (
            <div
              className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center rounded"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 99,
                top: 0,
                left: 0,
              }}
            >
              <FullScreenLoader />
            </div>
          )}
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            firstDay={1}
            events={events}
            datesSet={handleDatesSet}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            headerToolbar={{
              right: "prev today next",
              left: "title",
            }}
            height="auto"
          />
        </div>
      </>
    </div>
  );
};

export default InstalltionCalnderView;
